import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { Form, Field } from "react-final-form";
import axios from "axios";

const API_KEY = "AIzaSyCKfUSr5PacGOhWURYgZlgjmgGh4Oc5kjE";

interface ConsentBundelPlaylistModelProps {
  open: boolean;
  onClose: () => void;
  listId: number | null;
  consentBundleId: number | null;
  onSuccess: (data?: any) => void;
  onUploadStart?: () => void; // 👈 added for parent modal trigger
}

const ConsentBundelPlaylistModel: React.FC<ConsentBundelPlaylistModelProps> = ({
  open,
  onClose,
  listId,
  consentBundleId,
  onSuccess,
  onUploadStart,
}) => {
  const [videoDetails, setVideoDetails] = useState({
    title: "",
    description: "",
    thumbnail: "",
    duration: "",
  });
  const [url, setUrl] = useState("");

  /* ----------------------- Extract Video ID ----------------------- */
  const extractVideoId = (url: string) => {
    const regex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  /* ----------------------- Fetch YouTube Details ----------------------- */
  const getVideoDetails = async (videoId: string) => {
    const ytUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${API_KEY}`;
    try {
      const response = await fetch(ytUrl);
      const data = await response.json();
      if (data.items.length > 0) {
        const videoData = data.items[0];
        const title = videoData.snippet.title;
        const description = videoData.snippet.description;
        const thumbnail = videoData.snippet.thumbnails.high.url;
        const rawDuration = videoData.contentDetails.duration;
        const duration = formatISODuration(rawDuration);
        return { title, description, thumbnail, duration };
      } else {
        throw new Error("Video not found");
      }
    } catch (error) {
      console.error("Error fetching video details:", error);
      return null;
    }
  };

  /* ----------------------- Handle Details Fetch ----------------------- */
  const handleScrubVideoDetails = async (url: string, form: any) => {
    const videoId = extractVideoId(url);
    if (videoId) {
      const details = await getVideoDetails(videoId);
      if (details) {
        setVideoDetails(details);
        form.change("title", details.title);
        form.change("description", details.description);
      } else {
        resetVideoDetails(form);
      }
    } else {
      resetVideoDetails(form);
    }
  };

  const resetVideoDetails = (form: any) => {
    form.change("title", "");
    form.change("description", "");
    setVideoDetails({ title: "", description: "", thumbnail: "", duration: "" });
  };

  /* ----------------------- Effects ----------------------- */
  useEffect(() => {
    if (url) handleScrubVideoDetails(url, {});
  }, [url]);

  useEffect(() => {
    if (!open) {
      setUrl("");
      setVideoDetails({ title: "", description: "", thumbnail: "", duration: "" });
    }
  }, [open]);

  /* ----------------------- Validation ----------------------- */
  const validate = (values: any) => {
    const errors: any = {};
    if (!values.url) errors.url = "URL required";
    return errors;
  };

  /* ----------------------- Format Duration ----------------------- */
  const formatISODuration = (isoDuration: string): string => {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = isoDuration.match(regex);
    if (!matches) return "0:00";
    const hours = parseInt(matches[1] || "0", 10);
    const minutes = parseInt(matches[2] || "0", 10);
    const seconds = parseInt(matches[3] || "0", 10);
    return hours > 0
      ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      : `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  /* ----------------------- Submit YouTube Video ----------------------- */
  const submitDataYt = async (values: any, onSuccess: () => void) => {
    const payload = {
      title: values.title || videoDetails.title,
      description: values.description || videoDetails.description,
      url: url,
      duration: videoDetails.duration,
      thumbnail: videoDetails.thumbnail,
      platform: "youtube",
    };

    try {
      if (onUploadStart) onUploadStart(); // 👈 tell parent to show modal
      const response = await axios.put(
        `consent_bundle/${consentBundleId}/add_video`,
        payload
      );
      if (response.status === 200) {
        onClose();
        onSuccess(response.data);
        console.log("✅ Video added successfully:", response.data);
      } else {
        console.error("Failed to add video:", response.data);
      }
    } catch (error) {
      console.error("Error submitting data:", error);
    }
  };

  /* ----------------------- Render Dialog ----------------------- */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "650px",
          borderRadius: "15px",
          padding: "20px",
          maxWidth: "100%",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
        Add New YouTube Video
      </DialogTitle>

      <DialogContent>
        <Form
          onSubmit={(values) => submitDataYt(values, onSuccess)}
          validate={validate}
          initialValues={{}}
          render={({ handleSubmit, form, values }) => (
            <form
              onSubmit={async (e) => {
                await handleSubmit(e);
                form.reset();
              }}
            >
              <Box sx={{ mb: 2 }}>
                {/* URL Field */}
                <Field name="url">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label="YouTube Video URL"
                      fullWidth
                      required
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        input.onChange(e);
                      }}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                      sx={{
                        mb: 2,
                        backgroundColor: "#f4f6f8",
                        borderRadius: "8px",
                      }}
                    />
                  )}
                </Field>

                {/* Thumbnail Preview */}
                {videoDetails.thumbnail && (
                  <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                    <img
                      src={videoDetails.thumbnail}
                      alt="Video Thumbnail"
                      style={{ width: "100%", borderRadius: "8px" }}
                    />
                  </Box>
                )}

                {/* Title & Description */}
                {url && videoDetails.thumbnail && (
                  <>
                    <Field name="title">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          label="Video Title"
                          fullWidth
                          value={values.title || videoDetails.title}
                          onChange={(e) => input.onChange(e)}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          sx={{
                            mb: 2,
                            backgroundColor: "#f4f6f8",
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </Field>

                    <Field name="description">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          value={values.description || videoDetails.description}
                          onChange={(e) => input.onChange(e)}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          sx={{
                            mb: 2,
                            backgroundColor: "#f4f6f8",
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </Field>
                  </>
                )}
              </Box>

              {/* Dialog Actions */}
              <DialogActions>
                <Button
                  onClick={() => {
                    setUrl("");
                    setVideoDetails({
                      title: "",
                      description: "",
                      thumbnail: "",
                      duration: "",
                    });
                    onClose();
                  }}
                  color="primary"
                >
                  Cancel
                </Button>

                <Button type="submit" variant="contained" color="primary">
                  Add
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConsentBundelPlaylistModel;
